import { useCallback, useRef } from 'react'
import { buildErrorMessage, getCommentType, getCommentOrientation } from '@constants'
import { AxiosError } from 'axios'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { useCommentsStore } from '@editorStores'
import { getComments } from '@queries'
import {
  createComment,
  deleteComment,
  updateComment,
  CommentCreateDTO,
  CommentModifyDTO,
} from '@mutations'
import { usePersistentParams } from 'src/hooks'

/**
 * The update endpoint validates the comment body as a whole (every field is
 * required server-side, despite `CommentModifyDTO` marking them optional). So a
 * partial PATCH is rejected with a 422 – callers must always send the full
 * comment.
 */
export const commentToModifyDTO = (comment: Comment3D): CommentModifyDTO => ({
  position: comment.position,
  text: comment.text,
  type: getCommentType(comment),
  orientation: getCommentOrientation(comment),
  color: comment.color,
  backgroundColor: comment.backgroundColor,
  showTitle: comment.showTitle,
  storeysForPlans: comment.storeysForPlans,
  showInPlans: comment.showInPlans,
  callouts: comment.callouts ?? null,
  storeyForSceneRendering: comment.storeyForSceneRendering ?? null,
  element_guid: comment.element_guid ?? null,
})

/**
 * Shared create/update/delete wiring for the in-scene comment tooling.
 *
 * Each server request runs through React Query's `useMutation` with a
 * `mutationKey` and `meta` (title/description), so the global `MutationCache`
 * surfaces it as a tracked interaction in the Ereignisse/Aktivitäten widget.
 *
 * Updates are RACE-SAFE: because the API replaces the whole comment on every
 * PATCH, naive concurrent saves would (a) clobber each other's fields and (b)
 * apply out of order. So `updateCommentFields`:
 *
 *  1. optimistically writes the change to the local store immediately, and
 *  2. serializes the PATCHes through a promise chain, building each request body
 *     from the LATEST store state at send time.
 *
 * That makes every request cumulative (it carries all prior optimistic changes)
 * and ordered (responses can't arrive out of order), so dragging a comment and
 * then editing its text/position mid-save converges to the correct result.
 */
const useCommentMutations = () => {
  const setComments = useCommentsStore(state => state.setComments)
  const updateLocalComment = useCommentsStore(state => state.updateComment)
  const { projectId }: { projectId?: string } = usePersistentParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()

  // Serialization chain + per-comment coalescing of pending saves.
  const chainRef = useRef<Promise<unknown>>(Promise.resolve())
  const pendingIdsRef = useRef<Set<string>>(new Set())

  const applyResult = useCallback(
    (comments: Comment3D[]) => {
      setComments(comments)
      queryClient.setQueryData(getComments.getKey(projectId), comments)
    },
    [setComments, queryClient, projectId],
  )

  const createMutation = useMutation<Comment3D[], unknown, CommentCreateDTO>(
    (data: CommentCreateDTO) => createComment.request(projectId, data),
    {
      mutationKey: ['createComment', projectId],
      meta: {
        title: 'Kommentar erstellen',
        description: 'Erstellt einen neuen Kommentar.',
      },
      onSuccess: comments => applyResult(comments),
      onError: error =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Erstellen des Kommentars'),
          { variant: 'error' },
        ),
    },
  )

  const updateMutation = useMutation<Comment3D[] | null, unknown, string>(
    (id: string) => {
      // Build the body from the LATEST store state at send time so coalesced
      // edits are cumulative. Guarded by updateCommentFields, but stay defensive
      // against a delete landing between enqueue and send.
      const comment = useCommentsStore.getState().comments.find(current => current.id === id)
      if (!comment) return Promise.resolve(null)
      return updateComment.request(projectId, id, commentToModifyDTO(comment))
    },
    {
      mutationKey: ['updateComment', projectId],
      meta: {
        title: 'Kommentar aktualisieren',
        description: 'Speichert Änderungen an einem Kommentar.',
      },
      // Deliberately DON'T write the response back into the store. The store is
      // already the source of truth (updated optimistically before the PATCH),
      // and it may hold newer, not-yet-sent edits. Overwriting it with this
      // response would clobber those edits and revert rapid successive changes
      // to the same comment (drag, then drag/edit again within one round-trip).
      // The query cache catches up on the next refetch; errors self-heal below.
      onError: async error => {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Aktualisieren des Kommentars'),
          { variant: 'error' },
        )
        // The optimistic change wasn't persisted – refetch the comments so the
        // store self-heals back to the authoritative server state.
        await queryClient.invalidateQueries({ queryKey: getComments.getKey(projectId) })
      },
    },
  )

  const deleteMutation = useMutation<Comment3D[], unknown, string, { previous: Comment3D[] }>(
    (id: string) => deleteComment.request(projectId, id),
    {
      mutationKey: ['deleteComment', projectId],
      meta: {
        title: 'Kommentar löschen',
        description: 'Löscht einen Kommentar.',
      },
      onMutate: id => {
        // Optimistically drop the comment so it disappears from the scene
        // immediately, before the server round-trip completes.
        const previous = useCommentsStore.getState().comments
        applyResult(previous.filter(comment => comment.id !== id))
        return { previous }
      },
      onSuccess: comments => applyResult(comments),
      onError: (error, _id, context) => {
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Löschen des Kommentars'),
          { variant: 'error' },
        )
        // The delete didn't persist – restore the comment from the snapshot.
        if (context?.previous) applyResult(context.previous)
      },
    },
  )

  // `mutate`/`mutateAsync` are stable across renders in react-query v4, so the
  // callbacks below stay referentially stable – important because the scene's
  // drag handlers depend on `updateCommentFields` and re-subscribe when it
  // changes.
  const { mutateAsync: runCreate } = createMutation
  const { mutateAsync: runUpdate } = updateMutation
  const { mutate: runDelete } = deleteMutation

  const createNewComment = useCallback(
    (data: CommentCreateDTO): Promise<Comment3D[]> => runCreate(data),
    [runCreate],
  )

  const updateCommentFields = useCallback(
    (id: string, changes: Partial<Comment3D>) => {
      // 1) Optimistic local update so the UI reflects the change instantly and
      //    later saves build on top of it.
      updateLocalComment(id, changes)

      // 2) Coalesce: if a save for this comment is already queued, the running
      //    task will pick up this latest state – no need to enqueue again.
      if (pendingIdsRef.current.has(id)) return
      pendingIdsRef.current.add(id)

      const task = async () => {
        pendingIdsRef.current.delete(id)
        if (!useCommentsStore.getState().comments.some(current => current.id === id)) return
        // Errors surface via the mutation's onError; swallow here so the
        // serialization chain keeps running.
        await runUpdate(id).catch(() => undefined)
      }

      chainRef.current = chainRef.current.catch(() => undefined).then(task)
    },
    [updateLocalComment, runUpdate],
  )

  const deleteCommentById = useCallback(
    (id: string) => {
      runDelete(id)
    },
    [runDelete],
  )

  return {
    createComment: createNewComment,
    updateCommentFields,
    deleteComment: deleteCommentById,
  }
}

export default useCommentMutations
